// src/portal/PortalShell.jsx
//
// The Operator Portal's own chrome. A THIRD SHELL, modelled on WorkspaceShell.
//
// ── WHY A THIRD SHELL AND NOT A FLAG ON AppShell ────────────────────────────
//
// WorkspaceShell's header makes this argument at length and every word of it
// applies here. In short: AppShell runs useOperatingAoiInit,
// useRestrictedCaseSelection, useRestrictedAutoEnableImagery,
// useRestrictedPadDrivenImagery, useRestrictedInitialLayers and
// useImpersonationFromUrl, and loads the project list. HOOKS RUN BEFORE ANY
// CONDITIONAL RETURN, so an `if (isPortal)` branch inside AppShell would still
// fire every one of them and collect 403s the submitter reads as a broken page.
//
// The shell is chosen STRUCTURALLY, by which layout route matched. router.jsx
// has three pathless layout routes under a bare root:
//
//   _platform   -> AppShell        everything that existed before
//   _workspace  -> WorkspaceShell  /workspace/*
//   _portal     -> this            /portal/*
//
// ── AND NOT LeftNavRail ─────────────────────────────────────────────────────
//
// LeftNavRail is a function inside App.jsx, rendered only by the platform
// shell. Threading a portal entry through it would import App.jsx and with it
// every hook above. A portal submitter has one surface and needs no rail.
//
// ── WHAT IT ASKS THE NETWORK FOR ────────────────────────────────────────────
//
// /auth/me, once, for the display name. That is on ALLOW_EXACT_PORTAL.
// PortalShell.fetches.test.jsx renders this and asserts the call HAPPENED and
// then that everything recorded is reachable by the portal class -- in that
// order, because a shell that renders without fetching passes the second check
// trivially.
import { Outlet, Link, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { LogOut } from "lucide-react";
import { useAuthStore } from "../stores/useAuthStore";

export default function PortalShell() {
  const loadProfile = useAuthStore((s) => s.loadProfile);
  const authToken = useAuthStore((s) => s.token);
  const profile = useAuthStore((s) => s.profile);

  useEffect(() => {
    if (!authToken) return;
    loadProfile();
  }, [authToken, loadProfile]);

  const path = useRouterState({ select: (s) => s.location.pathname });
  const onDetail = path.startsWith("/portal/");

  const who = profile?.display_name || profile?.email || "";

  return (
    <div className="flex h-screen min-h-0 flex-col bg-bg text-fg-primary">
      {/* h-11, matching WorkspaceShell for its stated reason: the map pane has
          a floor and every pixel of chrome comes out of it. */}
      <header className="flex h-11 shrink-0 items-center gap-4 border-b border-bg-border px-4">
        {onDetail ? (
          <Link to="/portal/plan-reviews" className="text-sm font-medium tracking-tight hover:text-white">
            Plan reviews
          </Link>
        ) : (
          <span className="text-sm font-medium tracking-tight text-fg-secondary">Plan reviews</span>
        )}

        <div className="flex-1" />

        {/* NO ORG SWITCHER. A submitter has one org and it is the customer's;
            offering a switch would imply otherwise. NO PROJECT SELECTOR: a plan
            review is not project-scoped, which is also why layerStaging's
            projectId became scopeId. NO NOTIFICATION BELL: portal notes write no
            collab_notifications row, and both notification endpoints are off
            this class's allowlist -- a bell here could only ever read zero. */}

        <div className="flex items-center gap-2 text-xs text-fg-secondary">
          <span className="max-w-[16rem] truncate" title={who}>{who}</span>
          <button
            type="button"
            onClick={() => window.__keycloak?.logout({ redirectUri: window.location.origin })}
            title="Sign out"
            className="transition-colors hover:text-fg-primary"
          >
            <LogOut size={14} />
          </button>
        </div>
      </header>

      <main className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </main>
    </div>
  );
}
